import BIRTHDAY_AUDIO_TRACKS from "config/birthdayAudioTracks";
import { buildYouTubeEmbedUrl, getYouTubeVideoRequest } from "utils/youtubeVideoUtils";

export const BIRTHDAY_AUDIO_SOURCE_TYPES = {
  NONE: "none",
  BUILTIN: "builtin",
  AUDIO: "audio",
  YOUTUBE: "youtube",
};

export const BIRTHDAY_AUDIO_NONE_LABEL = "None";

const BUILTIN_TRACKS_BY_ID = new Map(
  BIRTHDAY_AUDIO_TRACKS.map((track) => [track.id, track])
);

const isNonEmptyString = (value) => typeof value === "string" && value.trim() !== "";

export const getBuiltInBirthdayTrack = (trackId) => (
  BUILTIN_TRACKS_BY_ID.get(String(trackId || "")) || null
);

export const isBuiltInBirthdayTrackId = (trackId) => Boolean(getBuiltInBirthdayTrack(trackId));

export const getBirthdayAudioFileName = (fileUrl = "") => {
  const value = String(fileUrl || "").trim();
  if (!value) return "Custom Audio";

  try {
    const storagePath = decodeURIComponent(value.split("/o/")[1]?.split("?")[0] || "");
    const fileName = storagePath.split("/").filter(Boolean).pop();
    if (fileName) return fileName;
  } catch {
    // Fall back to URL parsing below.
  }

  try {
    const parsedUrl = new URL(value);
    const fileName = decodeURIComponent(parsedUrl.pathname.split("/").filter(Boolean).pop() || "");
    return fileName || "Custom Audio";
  } catch {
    return "Custom Audio";
  }
};

export const normalizeBirthdaySoundConfig = (config = {}) => {
  const sound = String(config?.sound || "none").trim() || "none";
  const soundSourceType = String(config?.soundSourceType || "").trim();
  const youtubeSrc = String(config?.youtubeSrc || "").trim();
  const customAudioSrc = String(config?.customAudioSrc || "").trim();
  const soundName = String(config?.soundName || "").trim();
  const hasCustomSoundName = soundName && soundName !== BIRTHDAY_AUDIO_NONE_LABEL;

  if (
    sound === "none"
    && !isNonEmptyString(youtubeSrc)
    && !isNonEmptyString(customAudioSrc)
  ) {
    return {
      ...config,
      sound: "none",
      soundSourceType: BIRTHDAY_AUDIO_SOURCE_TYPES.NONE,
      soundName: BIRTHDAY_AUDIO_NONE_LABEL,
      youtube: false,
      youtubeSrc: "",
      customAudio: false,
      customAudioSrc: "",
      customAudioName: "",
    };
  }

  const builtinTrack = getBuiltInBirthdayTrack(sound);
  if (builtinTrack) {
    return {
      ...config,
      sound,
      soundSourceType: BIRTHDAY_AUDIO_SOURCE_TYPES.BUILTIN,
      soundName: hasCustomSoundName ? soundName : builtinTrack.label,
      youtube: false,
      youtubeSrc: "",
      customAudio: false,
      customAudioSrc: "",
      customAudioName: "",
    };
  }

  const nextYoutubeSrc = youtubeSrc || (
    soundSourceType === BIRTHDAY_AUDIO_SOURCE_TYPES.YOUTUBE || config?.youtube
      ? sound
      : ""
  );
  if (nextYoutubeSrc && getYouTubeVideoRequest(nextYoutubeSrc)) {
    return {
      ...config,
      sound: nextYoutubeSrc,
      soundSourceType: BIRTHDAY_AUDIO_SOURCE_TYPES.YOUTUBE,
      soundName: hasCustomSoundName ? soundName : nextYoutubeSrc,
      youtube: true,
      youtubeSrc: nextYoutubeSrc,
      customAudio: false,
      customAudioSrc: "",
      customAudioName: "",
    };
  }

  const nextCustomAudioSrc = customAudioSrc || (
    soundSourceType === BIRTHDAY_AUDIO_SOURCE_TYPES.AUDIO || config?.customAudio || sound !== "none"
      ? sound
      : ""
  );
  if (nextCustomAudioSrc) {
    const nextCustomAudioName = String(config?.customAudioName || soundName || "").trim();
    return {
      ...config,
      sound: nextCustomAudioSrc,
      soundSourceType: BIRTHDAY_AUDIO_SOURCE_TYPES.AUDIO,
      soundName: hasCustomSoundName ? soundName : nextCustomAudioName || getBirthdayAudioFileName(nextCustomAudioSrc),
      youtube: false,
      youtubeSrc: "",
      customAudio: true,
      customAudioSrc: nextCustomAudioSrc,
      customAudioName: nextCustomAudioName || getBirthdayAudioFileName(nextCustomAudioSrc),
    };
  }

  return {
    ...config,
    sound: "none",
    soundSourceType: BIRTHDAY_AUDIO_SOURCE_TYPES.NONE,
    soundName: BIRTHDAY_AUDIO_NONE_LABEL,
    youtube: false,
    youtubeSrc: "",
    customAudio: false,
    customAudioSrc: "",
    customAudioName: "",
  };
};

export const resolveBirthdayAudioSource = (config = {}) => {
  const normalizedConfig = normalizeBirthdaySoundConfig(config);
  const { soundSourceType } = normalizedConfig;

  if (soundSourceType === BIRTHDAY_AUDIO_SOURCE_TYPES.BUILTIN) {
    const track = getBuiltInBirthdayTrack(normalizedConfig.sound);
    if (!track?.file) return { type: BIRTHDAY_AUDIO_SOURCE_TYPES.NONE, label: BIRTHDAY_AUDIO_NONE_LABEL };
    return {
      type: BIRTHDAY_AUDIO_SOURCE_TYPES.BUILTIN,
      url: track.file,
      label: normalizedConfig.soundName || track.label,
    };
  }

  if (soundSourceType === BIRTHDAY_AUDIO_SOURCE_TYPES.YOUTUBE) {
    const url = normalizedConfig.youtubeSrc || normalizedConfig.sound;
    const embedUrl = buildYouTubeEmbedUrl(url);
    if (!embedUrl) return { type: BIRTHDAY_AUDIO_SOURCE_TYPES.NONE, label: BIRTHDAY_AUDIO_NONE_LABEL };
    return {
      type: BIRTHDAY_AUDIO_SOURCE_TYPES.YOUTUBE,
      url,
      embedUrl,
      label: normalizedConfig.soundName || url,
    };
  }

  if (soundSourceType === BIRTHDAY_AUDIO_SOURCE_TYPES.AUDIO) {
    const url = normalizedConfig.customAudioSrc || normalizedConfig.sound;
    if (!url) return { type: BIRTHDAY_AUDIO_SOURCE_TYPES.NONE, label: BIRTHDAY_AUDIO_NONE_LABEL };
    return {
      type: BIRTHDAY_AUDIO_SOURCE_TYPES.AUDIO,
      url,
      label: normalizedConfig.soundName || normalizedConfig.customAudioName || getBirthdayAudioFileName(url),
    };
  }

  return { type: BIRTHDAY_AUDIO_SOURCE_TYPES.NONE, label: BIRTHDAY_AUDIO_NONE_LABEL };
};
